import React from 'react';

type TextVariant =
  | 'heading'
  | 'subheading'
  | 'body'
  | 'label'
  | 'caption'
  | 'overline'
  | 'metric';

interface TextOwnProps {
  variant?: TextVariant;
  as?: React.ElementType;
  className?: string;
  children?: React.ReactNode;
}

type TextProps<C extends React.ElementType = 'span'> = TextOwnProps &
  Omit<React.ComponentPropsWithRef<C>, keyof TextOwnProps>;

const VARIANT_STYLES: Record<TextVariant, { classes: string; defaultTag: React.ElementType }> = {
  heading: {
    classes: 'text-lg font-bold text-content',
    defaultTag: 'h2',
  },
  subheading: {
    classes: 'text-base font-semibold text-content',
    defaultTag: 'h3',
  },
  body: {
    classes: 'text-sm text-content-secondary leading-relaxed',
    defaultTag: 'p',
  },
  label: {
    classes: 'text-2xs text-content-muted uppercase tracking-wider',
    defaultTag: 'label',
  },
  caption: {
    classes: 'text-[11px] text-content-tertiary',
    defaultTag: 'span',
  },
  overline: {
    classes: 'text-2xs text-content-faint uppercase tracking-wider',
    defaultTag: 'span',
  },
  metric: {
    classes: 'text-2xl font-bold tabular-nums',
    defaultTag: 'span',
  },
};

function Text<C extends React.ElementType = 'span'>(
  { variant = 'body', as, className = '', children, ...rest }: TextProps<C>,
  ref: React.ForwardedRef<HTMLElement>,
) {
  const config = VARIANT_STYLES[variant];
  const Component = as || config.defaultTag;

  return (
    <Component ref={ref} className={`${config.classes} ${className}`} {...rest}>
      {children}
    </Component>
  );
}

const ForwardedText = React.forwardRef(Text) as <C extends React.ElementType = 'span'>(
  props: TextProps<C> & { ref?: React.ForwardedRef<HTMLElement> },
) => React.ReactElement | null;

export default ForwardedText;
export type { TextProps, TextVariant };
